"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
	DOWNLOAD_STAGGER_MS,
	FILE_STATUS,
	type FileStatus,
	MSG,
	WORKER_PATH,
	ZIP_FILENAME,
} from "@/constants/image-converter";
import { detectFormat, getPoolSize, isImageFile } from "@/utils/image-processor";
import { isHeicFile, decodeHeicToPng } from "@/utils/heic-decoder";
import { toast } from "sonner";
import logger from "@/lib/logger";

// --- Types ---

export interface ImageFile {
	id: string;
	file: File;
	name: string;
	size: number;
	inputFormat: string;
	status: FileStatus;
	outputBlob?: Blob;
	outputUrl?: string;
	outputName?: string;
	error?: string;
}

interface WorkerInfo {
	worker: Worker;
	busy: boolean;
	ready: boolean;
}

interface WorkerResultMessage {
	type: typeof MSG.RESULT;
	id: string;
	outputBuffer?: ArrayBuffer;
	outputName?: string;
	mimeType?: string;
	success: boolean;
	error?: string;
}

interface WorkerReadyMessage {
	type: typeof MSG.READY;
	error?: string;
}

type WorkerMessage = WorkerResultMessage | WorkerReadyMessage;

interface UseImageWorkerPoolReturn {
	files: ImageFile[];
	isProcessing: boolean;
	isReady: boolean;
	isLoading: boolean;
	overallProgress: number;
	completedCount: number;
	totalCount: number;
	error: string | null;
	addFiles: (files: FileList | File[]) => void;
	removeFile: (id: string) => void;
	clearFiles: () => void;
	startConversion: (outputFormat: string, quality: number) => void;
	cancelConversion: () => void;
	downloadAll: () => Promise<void>;
	downloadFile: (id: string) => void;
}

// --- Hook ---

export function useImageWorkerPool(): UseImageWorkerPoolReturn {
	const [files, setFiles] = useState<ImageFile[]>([]);
	const [isProcessing, setIsProcessing] = useState(false);
	const [isReady, setIsReady] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const workersRef = useRef<WorkerInfo[]>([]);
	const pendingQueueRef = useRef<string[]>([]);
	const filesRef = useRef<ImageFile[]>([]);
	const conversionParamsRef = useRef<{
		outputFormat: string;
		quality: number;
	} | null>(null);
	const cancelledRef = useRef(false);

	// Keep filesRef in sync with state
	useEffect(() => {
		filesRef.current = files;
	}, [files]);

	// Cleanup workers and object URLs on unmount
	useEffect(() => {
		return () => {
			for (const workerInfo of workersRef.current) {
				workerInfo.worker.terminate();
			}
			workersRef.current = [];
			for (const file of filesRef.current) {
				if (file.outputUrl) {
					URL.revokeObjectURL(file.outputUrl);
				}
			}
		};
	}, []);

	const updateFile = useCallback(
		(id: string, updates: Partial<ImageFile>) => {
			setFiles((prev) =>
				prev.map((f) => (f.id === id ? { ...f, ...updates } : f)),
			);
		},
		[],
	);

	const processNext = useCallback((workerInfo: WorkerInfo) => {
		if (cancelledRef.current) {
			workerInfo.busy = false;
			return;
		}

		const nextId = pendingQueueRef.current.shift();
		if (!nextId) {
			workerInfo.busy = false;
			const allIdle = workersRef.current.every((w) => !w.busy);
			if (allIdle) {
				setIsProcessing(false);
			}
			return;
		}

		const file = filesRef.current.find((f) => f.id === nextId);
		if (!file) {
			processNext(workerInfo);
			return;
		}

		workerInfo.busy = true;

		setFiles((prev) =>
			prev.map((f) =>
				f.id === nextId ? { ...f, status: FILE_STATUS.PROCESSING } : f,
			),
		);

		file.file.arrayBuffer().then(async (buffer) => {
			const params = conversionParamsRef.current;
			if (!params || cancelledRef.current) {
				workerInfo.busy = false;
				return;
			}

			let inputBuffer = buffer;
			let inputName = file.name;

			// Pre-decode HEIC/HEIF to PNG on the main thread.
			// FFmpeg's HEIF demuxer can't handle grid-based iPhone photos.
			// Native browser decoder (Chrome/Safari) or libheif-js (Firefox).
			if (isHeicFile(file.name)) {
				try {
					inputBuffer = await decodeHeicToPng(buffer);
					inputName = file.name.replace(/\.heic$/i, ".png").replace(/\.heif$/i, ".png");
				} catch (heicError) {
					const errorMsg = `HEIC decode failed: ${heicError instanceof Error ? heicError.message : "unsupported"}`;
					logger.error({ fileName: file.name, fileSize: file.size, err: heicError }, `[ImageWorkerPool] ${errorMsg}`);
					toast.error(`Failed to decode ${file.name}`, { description: "HEIC format could not be decoded. Try converting with a different tool first." });
					updateFile(nextId, {
						status: FILE_STATUS.ERROR,
						error: errorMsg,
					});
					processNext(workerInfo);
					return;
				}
			}

			workerInfo.worker.postMessage(
				{
					type: MSG.CONVERT,
					id: nextId,
					inputBuffer,
					inputName,
					outputFormat: params.outputFormat,
					quality: params.quality,
				},
				[inputBuffer],
			);
		});
	}, []);

	const handleWorkerMessage = useCallback(
		(workerInfo: WorkerInfo, e: MessageEvent<WorkerMessage>) => {
			const data = e.data;

			if (data.type === MSG.READY) {
				if (data.error) {
					logger.error({ err: data.error }, "[ImageWorkerPool] Worker init failed");
					workerInfo.ready = false;
					setError(data.error);
					toast.error("Image converter failed to load", { description: data.error });
					setIsLoading(false);
					setIsProcessing(false);
				} else {
					workerInfo.ready = true;
					const anyReady = workersRef.current.some((w) => w.ready);
					if (anyReady) {
						setIsReady(true);
						setIsLoading(false);
					}
					if (pendingQueueRef.current.length > 0 && !workerInfo.busy) {
						processNext(workerInfo);
					}
				}
				return;
			}

			if (data.type === MSG.RESULT) {
				if (data.success && data.outputBuffer) {
					const blob = new Blob([data.outputBuffer], {
						type: data.mimeType || "application/octet-stream",
					});
					const url = URL.createObjectURL(blob);

					updateFile(data.id, {
						status: FILE_STATUS.COMPLETED,
						outputBlob: blob,
						outputUrl: url,
						outputName: data.outputName,
					});
				} else {
					const errorMsg = data.error || "Unknown conversion error";
					const file = filesRef.current.find((f) => f.id === data.id);
					logger.error({ err: errorMsg, fileName: file?.name, fileSize: file?.size, inputFormat: file?.inputFormat }, "[ImageWorkerPool] Conversion failed");
					toast.error(`Failed to convert ${file?.name || "file"}`, { description: errorMsg });
					updateFile(data.id, {
						status: FILE_STATUS.ERROR,
						error: errorMsg,
					});
				}

				processNext(workerInfo);
			}
		},
		[processNext, updateFile],
	);

	const ensureWorkers = useCallback(() => {
		if (workersRef.current.length > 0) return;

		setIsLoading(true);
		const poolSize = getPoolSize();

		for (let i = 0; i < poolSize; i++) {
			const worker = new Worker(WORKER_PATH);
			const workerInfo: WorkerInfo = { worker, busy: false, ready: false };

			worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
				handleWorkerMessage(workerInfo, e);
			};

			worker.onerror = (err) => {
				logger.error({ err: err.message, filename: err.filename, lineno: err.lineno, colno: err.colno }, "[ImageWorkerPool] Worker crashed");
				const errorMsg = `Worker error: ${err.message}`;
				setError(errorMsg);
				toast.error("Image converter worker crashed", { description: err.message });
				workerInfo.busy = false;
				workerInfo.ready = false;
				setIsLoading(false);
				setIsProcessing(false);
			};

			workersRef.current.push(workerInfo);
			worker.postMessage({ type: MSG.INIT });
		}
	}, [handleWorkerMessage]);

	// --- Public API ---

	const addFiles = useCallback((newFiles: FileList | File[]) => {
		const fileArray = Array.from(newFiles).filter(isImageFile);
		if (fileArray.length === 0) return;

		const imageFiles: ImageFile[] = fileArray.map((file) => ({
			id: crypto.randomUUID(),
			file,
			name: file.name,
			size: file.size,
			inputFormat: detectFormat(file),
			status: FILE_STATUS.PENDING as FileStatus,
		}));

		setFiles((prev) => [...prev, ...imageFiles]);
	}, []);

	const removeFile = useCallback((id: string) => {
		setFiles((prev) => {
			const file = prev.find((f) => f.id === id);
			if (file?.outputUrl) {
				URL.revokeObjectURL(file.outputUrl);
			}
			return prev.filter((f) => f.id !== id);
		});
	}, []);

	const clearFiles = useCallback(() => {
		setFiles((prev) => {
			for (const file of prev) {
				if (file.outputUrl) {
					URL.revokeObjectURL(file.outputUrl);
				}
			}
			return [];
		});
	}, []);

	const startConversion = useCallback(
		(outputFormat: string, quality: number) => {
			if (files.length === 0) return;

			cancelledRef.current = false;
			conversionParamsRef.current = { outputFormat, quality };

			// Reset all files to pending
			setFiles((prev) =>
				prev.map((f) => {
					if (f.outputUrl) URL.revokeObjectURL(f.outputUrl);
					return {
						...f,
						status: FILE_STATUS.PENDING as FileStatus,
						outputBlob: undefined,
						outputUrl: undefined,
						outputName: undefined,
						error: undefined,
					};
				}),
			);

			pendingQueueRef.current = files.map((f) => f.id);
			setIsProcessing(true);
			setError(null);

			ensureWorkers();

			// Dispatch to ready workers after state updates propagate
			setTimeout(() => {
				for (const workerInfo of workersRef.current) {
					if (workerInfo.ready && !workerInfo.busy) {
						processNext(workerInfo);
					}
				}
			}, 0);
		},
		[files, ensureWorkers, processNext],
	);

	const cancelConversion = useCallback(() => {
		cancelledRef.current = true;
		pendingQueueRef.current = [];
		setIsProcessing(false);
	}, []);

	const downloadFile = useCallback((id: string) => {
		const file = filesRef.current.find((f) => f.id === id);
		if (!file?.outputUrl || !file.outputName) return;

		const a = document.createElement("a");
		a.href = file.outputUrl;
		a.download = file.outputName;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	}, []);

	const downloadAll = useCallback(async () => {
		const completedFiles = filesRef.current.filter(
			(f) =>
				f.status === FILE_STATUS.COMPLETED && f.outputBlob && f.outputName,
		);
		if (completedFiles.length === 0) return;

		try {
			const JSZip = (await import("jszip")).default;
			const zip = new JSZip();

			for (const file of completedFiles) {
				if (file.outputBlob && file.outputName) {
					zip.file(file.outputName, file.outputBlob);
				}
			}

			const zipBlob = await zip.generateAsync({ type: "blob" });
			const url = URL.createObjectURL(zipBlob);
			const a = document.createElement("a");
			a.href = url;
			a.download = ZIP_FILENAME;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		} catch {
			// Fallback to sequential individual downloads
			for (const [index, file] of completedFiles.entries()) {
				setTimeout(() => {
					if (file.outputUrl && file.outputName) {
						const a = document.createElement("a");
						a.href = file.outputUrl;
						a.download = file.outputName;
						document.body.appendChild(a);
						a.click();
						document.body.removeChild(a);
					}
				}, index * DOWNLOAD_STAGGER_MS);
			}
		}
	}, []);

	// --- Derived state ---

	const completedCount = files.filter(
		(f) => f.status === FILE_STATUS.COMPLETED,
	).length;
	const errorCount = files.filter(
		(f) => f.status === FILE_STATUS.ERROR,
	).length;
	const totalCount = files.length;
	const overallProgress =
		totalCount > 0
			? Math.round(((completedCount + errorCount) / totalCount) * 100)
			: 0;

	return {
		files,
		isProcessing,
		isReady,
		isLoading,
		overallProgress,
		completedCount,
		totalCount,
		error,
		addFiles,
		removeFile,
		clearFiles,
		startConversion,
		cancelConversion,
		downloadAll,
		downloadFile,
	};
}
